/**
 * useCardSnapshot - Captures a PNG of a preference card's DOM element and uploads
 * it to /api/cards/:card_id/snapshot, which stores it on Cloudinary and writes
 * snapshot_url onto the preference_cards document. The snapshot is what shows up
 * as the og:image when the card share link is unfurled in iMessage/WhatsApp/etc.
 *
 * Usage:
 *   const cardRef = useRef(null);
 *   const { captureAndUpload, capturing } = useCardSnapshot(cardRef, cardId);
 *   // <div ref={cardRef}>...your card jsx...</div>
 *   // call captureAndUpload() after the card saves
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { toPng } from "html-to-image";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useCardSnapshot(cardRef, cardId) {
  const [capturing, setCapturing] = useState(false);

  const captureAndUpload = useCallback(async (opts = {}) => {
    if (!cardRef?.current || !cardId) return null;
    setCapturing(true);
    try {
      // Capture only the card element (not the page)
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: opts.backgroundColor || "#ffffff",
        // Filter out elements marked data-snapshot-exclude (e.g., edit buttons, hover overlays)
        filter: (node) => {
          if (!node || !node.getAttribute) return true;
          return node.getAttribute("data-snapshot-exclude") !== "true";
        },
        // Skip fetching external fonts to avoid CORS issues
        skipFonts: true,
      });

      const res = await axios.post(`${API}/cards/${cardId}/snapshot`, {
        image_data: dataUrl,
      });
      return res.data?.snapshot_url || null;
    } catch (err) {
      console.error("Snapshot capture failed:", err);
      return null;
    } finally {
      setCapturing(false);
    }
  }, [cardRef, cardId]);

  return { captureAndUpload, capturing };
}

export default useCardSnapshot;
